import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { addDays, addMinutes, differenceInMinutes, format, getDayOfYear, isValid } from "date-fns";
import {
  CalendarDays,
  Clock,
  Flag,
  FolderOpen,
  Mail,
  NotepadText,
  Phone,
  Smile,
  Clock1,
  Clock2,
  Clock3,
  Clock4,
  Clock5,
  Clock6,
  Clock7,
  Clock8,
  Clock9,
  Clock10,
  Clock11,
  Clock12,
  CircleAlert,
} from "lucide-react";
import { fromJS } from "immutable";
import { searchActivities } from "../api/ProjectApi";
import ActivityResizable from "./ActivityResizable";
import { ActivityStatus } from "./ActivityIndicator";
import { ActivityTypes } from "../constants/action-types";
import { getBaseUrl } from "../api/api";

export const TOTAL_MIN_PER_DAY = 1440;
export const MIN_PER_ROW = 5;

const today = new Date();

export const buildTimeValues = (minStep = 15, labelTimeFormat = "hh:mm aaa") => {
  const timeSeries = [];
  const now = new Date();
  const todayString = format(now, "yyyy-MM-dd");
  const total = TOTAL_MIN_PER_DAY / minStep;
  let start = new Date(`${todayString}T00:00:00`);
  for (let index = 1; index <= total; index++) {
    const item = {
      label: format(start, labelTimeFormat),
      value: format(start, "HH:mm:ss"),
      distanceToNow: differenceInMinutes(start, now),
      start,
    };
    timeSeries.push(item);
    start = addMinutes(start, minStep);
  }

  return timeSeries;
};

const isOverdueActivity = (activity) => {
  const start = new Date(
    `${activity.getIn(["event", "data", "start"])}T${activity.getIn([
      "event",
      "data",
      "startTime",
    ])}`
  );
  const startInDay = isValid(start) ? getDayOfYear(start) : 0;
  const todayInDay = getDayOfYear(today);
  return startInDay < todayInDay;
};

const getColor = (activity, filter) => {
  const eventId = activity.get("id") || "new";
  let color = eventId == "new" ? "gray" : "blue";
  const eventStatus = activity.getIn(["event", "data", "done"]) || false;
  if (eventStatus) {
    color = "blue";
  } else if (isOverdueActivity(activity)) {
    color = "red";
  } else if (filter == "Tomorrow") {
    color = "gray";
  }
  return color;
};

const renderActivityIcon = (type) => {
  switch (type) {
    case ActivityTypes.email:
    case ActivityTypes.logEmail:
    case ActivityTypes.sendEmail:
    case ActivityTypes.sendDraftEmail:
    case ActivityTypes.sendScheduledEmail:
    case ActivityTypes.recordSentEmail:
      return <Mail className="h-3 w-3" aria-hidden="true" />;
    case ActivityTypes.meeting:
      return <CalendarDays className="h-3 w-3" aria-hidden="true" />;
    case ActivityTypes.call:
      return <Phone className="h-3 w-3" aria-hidden="true" />;
    case ActivityTypes.deadline:
      return <Flag className="h-3 w-3" aria-hidden="true" />;
    case ActivityTypes.task:
      return <Clock className="h-3 w-3" aria-hidden="true" />;
    case ActivityTypes.commented:
      return <NotepadText className="h-3 w-3" aria-hidden="true" />;
    case ActivityTypes.file:
      return <FolderOpen className="h-3 w-3" aria-hidden="true" />;
    case ActivityTypes.lunch:
      return <Smile className="h-3 w-3" aria-hidden="true" />;
  }
};

const ActivityEvent = ({
  filter,
  activity,
  onRenderEvent,
  onClickItem,
  onClickEvent,
  onClickMarkAsDone,
  viewType,
}) => {
  const { t } = useTranslation();

  const eventId = activity.get("id") || "new";
  const isNewEvent = eventId == "new";
  const hasRenderedRef = useRef(false);

  useEffect(() => {
    if (!hasRenderedRef.current) {
      onRenderEvent();
      hasRenderedRef.current = true;
    }
  }, [onRenderEvent]);

  const startOfDay = new Date(`${activity.getIn(["event", "data", "start"])}T00:00:00`);
  const start = new Date(
    `${activity.getIn(["event", "data", "start"])}T${activity.getIn([
      "event",
      "data",
      "startTime",
    ])}`
  );
  const end = new Date(
    `${activity.getIn(["event", "data", "end"])}T${activity.getIn(["event", "data", "endTime"])}`
  );
  if (!isValid(start)) {
    return null;
  }

  const startTimeText = isValid(start) ? format(start, "hh:mm aa") : "";
  const endTimeText = isValid(end) ? format(end, "hh:mm aa") : "";

  const item = activity.getIn(["event", "data", "item"]) || fromJS({});
  const duration = Math.abs(differenceInMinutes(start, end));
  const spanNum = Math.round(duration / MIN_PER_ROW);
  const rowNum = Math.round(Math.abs(differenceInMinutes(startOfDay, start)) / MIN_PER_ROW);
  const summary = activity.getIn(["event", "data", "summary"]) || "";

  const color = getColor(activity, filter);
  const handleEventResize = () => {};

  const renderClockByTime = () => {
    switch (start.getHours()) {
      case 1:
        return <Clock1 className="h-4 w-4" aria-hidden="true" />;
      case 2:
        return <Clock2 className="h-4 w-4" aria-hidden="true" />;
      case 3:
        return <Clock3 className="h-4 w-4" aria-hidden="true" />;
      case 4:
        return <Clock4 className="h-4 w-4" aria-hidden="true" />;
      case 5:
        return <Clock5 className="h-4 w-4" aria-hidden="true" />;
      case 6:
        return <Clock6 className="h-4 w-4" aria-hidden="true" />;
      case 7:
        return <Clock7 className="h-4 w-4" aria-hidden="true" />;
      case 8:
        return <Clock8 className="h-4 w-4" aria-hidden="true" />;
      case 9:
        return <Clock9 className="h-4 w-4" aria-hidden="true" />;
      case 10:
        return <Clock10 className="h-4 w-4" aria-hidden="true" />;
      case 11:
        return <Clock11 className="h-4 w-4" aria-hidden="true" />;
      case 12:
        return <Clock12 className="h-4 w-4" aria-hidden="true" />;
      default:
        return <Clock className="h-4 w-4" aria-hidden="true" />;
    }
  };

  const renderIconTitle = () => {
    if (activity.getIn(["event", "data", "hasGmail"])) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/google.png`}
          alt="Google"
        />
      );
    }
    if (activity.getIn(["event", "data", "hasOutlook"])) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/microsoft.png`}
          alt="Microsoft"
        />
      );
    }
    return null;
  };

  const renderBaseView = () => {
    return (
      <div className="w-full flex items-center space-x-2">
        {!isNewEvent && (
          <div className={"w-fit"}>
            <ActivityStatus
              activity={activity}
              itemId={activity.get("itemId")}
              onClickMarkAsDone={onClickMarkAsDone}
            />
          </div>
        )}
        <div className="w-full space-y-1">
          <div
            className={`w-full pt-1 flex items-center gap-2 text-${color}-500 group-hover:text-${color}-700`}
          >
            {renderClockByTime()}
            <div className="font-medium">
              {activity.getIn(["event", "data", "activityType"]) === ActivityTypes.deadline
                ? startTimeText
                : `${startTimeText} - ${endTimeText}`}
            </div>
          </div>
          <div className="w-full flex items-center space-x-1">
            {renderIconTitle()}
            <div
              title={summary}
              className={`w-full font-semibold text-${color}-700 truncate cursor-pointer`}
              onClick={() => {
                onClickEvent(activity);
              }}
            >
              {summary}
            </div>
          </div>
          <div className="w-full inline-flex items-center space-x-1">
            <div
              className={`flex items-center rounded-lg text-${color}-700 border border-gray-300 px-1 py-0.5 space-x-1`}
            >
              {renderActivityIcon(activity.getIn(["event", "data", "activityType"]))}
              <div className="text-xs capitalize">
                {activity.getIn(["event", "data", "activityType"])}
              </div>
            </div>
            <div
              title={item.get("label")}
              className={`text-xs font-semibold text-${color}-500 truncate cursor-pointer underline`}
              onClick={() => {
                if (!isNewEvent) {
                  onClickItem(item.toJS());
                }
              }}
            >
              {item.get("label")}
            </div>
          </div>
          {!activity.getIn(["event", "data", "hasGmail"]) &&
          !activity.getIn(["event", "data", "hasOutlook"]) ? (
            <div className="w-full inline-flex items-center space-x-1 bg-yellow-200 text-yellow-90 rounded-lg">
              <CircleAlert className="h-4 w-4 text-yellow-900" aria-hidden="true" />
              <div
                className="text-yellow-900 truncate"
                title={t("Hint: Connect to sync with your Google/Microsoft account")}
              >
                {t("Hint: Connect to sync with your Google/Microsoft account")}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };
  const renderTimeEvent = () => {
    return (
      <li
        key={`activity_${activity.get("id")}`}
        className="relative mt-px flex"
        style={{ gridRow: `${rowNum} / span ${spanNum}` }}
      >
        <ActivityResizable
          color={color}
          onResize={() => {
            handleEventResize();
          }}
        >
          {renderBaseView()}
        </ActivityResizable>
      </li>
    );
  };
  const renderListEvent = () => {
    return (
      <div className={`rounded-sm bg-${color}-50 px-2 text-xs leading-5 hover:bg-${color}-100`}>
        {renderBaseView()}
      </div>
    );
  };
  if (viewType == "time") {
    return renderTimeEvent();
  }
  return renderListEvent();
};

const CalendarTimeline = ({
  itemId,
  eventData,
  height,
  viewType = "time",
  filter = "Today",
  /** When set with scope "item", loads activities for COMPANY/CONTACT/SALE together with linked records. */
  anchorItemType,
  onClickItem = ({}: any) => {},
  onClickEvent = ({}: any) => {},
}) => {
  const container = useRef(null);
  const navTimelines = buildTimeValues(60, "hh aa");
  const [activities, setActivities] = useState(fromJS([]));
  const [newActivity, setNewActivity] = useState(fromJS({}));
  const [initCurrentTime, setInitCurrentTime] = useState(false);

  const getPosition = (date) => {
    const currentMinute = date.getHours() * 60;
    const appHeight = container.current.scrollHeight;
    return (appHeight * currentMinute) / TOTAL_MIN_PER_DAY;
  };

  const goToCurrentTime = (date) => {
    // Set the container scroll position based on the current time.
    container.current.scrollTop = getPosition(date);
  };

  const loadActivities = () => {
    if (itemId) {
      searchActivities({
        itemId,
        scope: "item",
        eventActions: "createActivity",
        ...buildFilter(),
        includeDependencies:
          anchorItemType === "COMPANY" || anchorItemType === "CONTACT" || anchorItemType === "SALE",
      }).then((data) => {
        setActivities(fromJS(data));
      });
    } else {
      setActivities(fromJS([]));
    }
  };

  useEffect(() => {
    setNewActivity(
      fromJS({
        event: {
          data: eventData,
        },
      })
    );
  }, [eventData]);

  useEffect(() => {
    const start = newActivity.getIn(["event", "data", "start"]);
    const startTime = newActivity.getIn(["event", "data", "startTime"]);
    if (start && startTime) {
      const startDateTime = new Date(`${start}T${startTime}`);
      goToCurrentTime(startDateTime);
    } else {
      goToCurrentTime(new Date());
    }
  }, [initCurrentTime, filter, newActivity]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildFilter = () => {
    if (filter == "Today") {
      return {
        days: format(today, "yyyy-MM-dd"),
        status: "false",
      };
    }
    if (filter == "Tomorrow") {
      return {
        days: `${format(addDays(today, 1), "yyyy-MM-dd")}`,
        status: "false",
      };
    }
    if (filter == "To-do" || filter == "Overdue") {
      return {
        days: "",
        status: "false",
      };
    }
    if (filter == "Done") {
      return {
        days: "",
        status: "true",
      };
    }
  };

  const renderEvent = (activity) => {
    return (
      <ActivityEvent
        key={`event_${activity.get("id")}`}
        viewType={viewType}
        activity={activity}
        onClickItem={onClickItem}
        onClickEvent={onClickEvent}
        onClickMarkAsDone={() => {
          loadActivities();
        }}
        onRenderEvent={() => {
          if (!initCurrentTime) {
            setInitCurrentTime(true);
          }
        }}
        filter={filter}
      />
    );
  };

  const getEventItems = () => {
    if (filter == "Overdue") {
      return activities.filter(isOverdueActivity);
    }
    return activities;
  };

  const renderEvents = () => {
    return getEventItems().map((activity) => renderEvent(activity));
  };

  if (viewType == "list") {
    return (
      <div className={"space-y-2 px-2 py-4"} ref={container}>
        {getEventItems().map((activity) => renderEvent(activity))}
      </div>
    );
  }

  return (
    <div
      ref={container}
      className="flex flex-auto flex-col overflow-auto min-w-80"
      style={{ height, maxHeight: 1200 }}
    >
      <div className="pt-4 flex w-full flex-auto">
        <div className="w-14 flex-none bg-white" />
        <div className="grid flex-auto grid-cols-1 grid-rows-1">
          {/* Horizontal lines */}
          <div
            className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
            style={{ gridTemplateRows: "repeat(24, minmax(6rem, 1fr))" }}
          >
            {navTimelines.map((timeline) => (
              <div
                key={`timeline_${timeline.value}`}
                className="sticky left-0 -ml-10 -mt-2.5 text-left text-xs leading-5 text-gray-400"
              >
                {timeline.label}
              </div>
            ))}
          </div>

          {/* Events */}
          <ol
            className="col-start-1 col-end-2 row-start-1 grid grid-cols-1"
            style={{
              gridTemplateRows: "1rem repeat(288, minmax(1rem, 1fr)) auto",
            }}
          >
            {renderEvent(newActivity)}
            {renderEvents()}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default CalendarTimeline;
